import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { http, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { Gem, Sparkles } from "lucide-react";
import ETransferPanel from "@/components/ETransferPanel";

export default function Account() {
  const { user, ready, refresh } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [site, setSite] = useState(null);
  const [etransfer, setEtransfer] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (ready && !user) nav("/login", { state: { redirect: "/account" } });
  }, [ready, user, nav]);

  useEffect(() => {
    http.get("/tiers").then((r) => setTiers(r.data));
    http.get("/site").then((r) => setSite(r.data));
    if (user) http.get("/bookings/mine").then((r) => setBookings(r.data)).catch(() => {});
  }, [user]);

  const subscribe = async (tierId) => {
    try {
      const { data } = await http.post("/payments/subscription", {
        tier_id: tierId,
        origin_url: window.location.origin,
      });
      setEtransfer({
        type: "subscription",
        amount: data.amount,
        client_email: data.client_email,
        reference_id: data.tx_id,
        label: data.label,
        details: data.label,
      });
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  if (!user) return null;

  const activeExp = user.subscription_expires_at
    ? new Date(user.subscription_expires_at).toLocaleDateString()
    : null;
  const activeTier = tiers.find((t) => t.id === user.subscription_tier);

  return (
    <div className="pt-32 pb-24" data-testid="account-page">
      <div className="max-w-6xl mx-auto px-6 sm:px-12 lg:px-20">
        <div className="mb-10">
          <div className="text-[10px] tracking-[0.35em] uppercase text-pink-blush mb-3">Welcome back, {user.name}</div>
          <h1 className="font-display text-5xl lg:text-6xl text-champagne">
            Your <span className="italic text-pink-hot">private</span> area
          </h1>
        </div>

        {/* Current subscription */}
        <div className="glass-panel rounded-lg p-8 mb-10" data-testid="current-subscription">
          <div className="flex justify-between items-start flex-wrap gap-4">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-pink-blush mb-2">Membership</div>
              <div className="font-display italic text-3xl text-champagne">
                {activeTier ? activeTier.name : "No active membership"}
              </div>
              {activeExp && (
                <div className="text-sm text-champagne/70 mt-1">Renews or expires on {activeExp}</div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Gem className={`w-6 h-6 ${activeTier ? "text-pink-hot" : "text-champagne/30"}`} />
              <div className={`text-sm ${activeTier ? "text-pink-hot" : "text-champagne/50"}`}>
                {activeTier ? "Diamond Circle active" : "Subscribe to unlock"}
              </div>
            </div>
          </div>
        </div>

        {/* Tier grid */}
        <div className="mb-16">
          <div className="text-[10px] tracking-[0.3em] uppercase text-pink-blush mb-3">Upgrade your access</div>
          <div className="grid md:grid-cols-3 gap-5">
            {tiers.map((t, i) => (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className={`glass-panel rounded-lg p-7 relative ${
                  activeTier?.id === t.id ? "border-pink-hot/60 glow-pink" : ""
                }`}
                data-testid={`tier-card-${t.id}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4" style={{ color: t.accent }} />
                  <div className="text-[10px] tracking-[0.3em] uppercase" style={{ color: t.accent }}>{t.name}</div>
                </div>
                <div className="font-display text-5xl text-champagne mb-1">${t.price.toFixed(0)}</div>
                <div className="text-xs text-champagne/60 mb-5">per {t.duration_days} days</div>
                <p className="text-sm text-champagne/75 mb-5">{t.description}</p>
                <ul className="space-y-2 mb-7 text-sm text-champagne/80">
                  {t.perks.map((p, idx) => (
                    <li key={idx} className="flex gap-2 items-start">
                      <span className="text-pink-hot mt-1">✦</span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => subscribe(t.id)}
                  className="w-full py-3 bg-pink-hot text-white text-xs tracking-[0.28em] uppercase rounded-sm hover:bg-pink-glow glow-pink transition-all"
                  data-testid={`subscribe-${t.id}`}
                >
                  {activeTier?.id === t.id ? "Extend" : "Subscribe"}
                </button>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Bookings */}
        <div className="glass-panel rounded-lg p-8" data-testid="my-bookings">
          <div className="text-[10px] tracking-[0.3em] uppercase text-pink-blush mb-4">My Bookings</div>
          {bookings.length === 0 ? (
            <div className="text-sm text-champagne/60">
              No bookings yet. <Link to="/contact" className="text-pink-hot pink-underline-link">Reserve an evening →</Link>
            </div>
          ) : (
            <div className="divide-y divide-pink-hot/10">
              {bookings.map((b) => (
                <div key={b.id} className="py-4 flex justify-between items-center flex-wrap gap-3">
                  <div>
                    <div className="font-display italic text-xl text-champagne">{b.rate_title}</div>
                    <div className="text-xs text-champagne/60 mt-1">{b.date} · {b.time}</div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`text-[10px] tracking-[0.25em] uppercase px-3 py-1 rounded-sm ${
                      b.payment_status === "paid" ? "bg-pink-hot/20 text-pink-hot" : "bg-rosegold/20 text-rosegold"
                    }`}>
                      {b.payment_status === "paid" ? "Confirmed" : "Pending"}
                    </span>
                    <div className="text-sm text-champagne/80">${b.deposit_amount}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
